import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { IField } from '../model/field';
import * as moment from 'moment';
import { cloneDeep, get, isNil, omit } from 'lodash';
import { SafeUrl } from '@angular/platform-browser';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { catchError, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

export const SANITIZE_HTML_CONFIG = {
  allowedTags: [
    'b',
    'i',
    'u',
    'strong',
    'strike',
    'br',
    'span',
    'em',
    'p',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'sup',
    'sub',
    'code',
    'blockquote',
    'div',
    'pre',
    'ol',
    'ul',
    'li',
    'a',
    'img',
    'table',
    'tr',
    'td',
    'hr',
    'iframe',
  ],
  allowedAttributes: {
    '*': ['style'],
    a: ['href', 'target', 'rel'],
    img: ['src', 'alt'],
    div: ['data-substitutions'],
    iframe: ['width', 'height', 'src', 'frameborder'],
  },
  allowedStyles: {
    '*': {
      'font-family': [/^.*$/],
      'font-size': [/^.*$/],
      height: [/^.*$/],
      margin: [/^.*$/],
      'margin-top': [/^.*$/],
      'margin-right': [/^.*$/],
      'margin-bottom': [/^.*$/],
      'margin-left': [/^.*$/],
      padding: [/^.*$/],
      'padding-top': [/^.*$/],
      'padding-right': [/^.*$/],
      'padding-bottom': [/^.*$/],
      'padding-left': [/^.*$/],
      'text-align': [/^.*$/],
      'text-decoration': [/^.*$/],
      'text-transform': [/^.*$/],
      width: [/^.*$/],
      color: [/^.*$/],
      'background-color': [/^.*$/],
      border: [/^.*$/],
      'border-style': [/^.*$/],
      'border-width': [/^.*$/],
      'border-color': [/^.*$/],
      'border-top-style': [/^.*$/],
      'border-right-style': [/^.*$/],
      'border-bottom-style': [/^.*$/],
      'border-left-style': [/^.*$/],
    },
  },
  selfClosing: ['img', 'br', 'hr'],
  allowedSchemes: ['http', 'https'],
  allowedSchemesByTag: {
    img: ['data'],
  },
};
declare const sanitizeHtml;
export interface ISharedSettings {
  contactType?: string;
  logoPosition?: string;
  background?: string;
  logoBase64?: SafeUrl;
  defaultContent?: string;
  footerContent?: string;
  headerContent?: string;
  successContent?: string;
}

export interface IFileData {
  name: string;
  mimeType: string;
  size: number;
}

// had to export this value so it can be used on utils.
export let language: string;

@Injectable({
  providedIn: 'root',
})
export class ContactSelfServiceFormsService {
  enteredEmail$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private siteKey: string | null = null;
  private isDomainNotAupr: boolean = false;
  private recaptchaKeysFailure: boolean = false; // failed to fetch site or secret key from server

  get getSiteKey(): string | null {
    return this.siteKey;
  }

  get getIsDomainNotAupr(): boolean {
    return this.isDomainNotAupr;
  }

  get getRecaptchaKeysStatus(): boolean {
    return this.recaptchaKeysFailure;
  }

  baseUrl: string = environment.externalServiceUrl;
  private sharedFormSettings = new BehaviorSubject({});
  currentsharedFormSettings = this.sharedFormSettings.asObservable();
  private contactTypeData = new BehaviorSubject('');
  currentcontactTypeData = this.contactTypeData.asObservable();
  constructor(private http: HttpClient, private translate: TranslateService) {}

  static sanitizeHtml(value: string): string {
    return sanitizeHtml(value, SANITIZE_HTML_CONFIG);
  }

  fetchSchema<T>(
    screen: string,
    contactType: string,
    language: string,
    email: string = null,
    token: string = null
  ): Observable<T> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'en-namespace': environment.namespace,
        browserLanguage: language,
      }),
    };
    const url =
      this.baseUrl + `contact/formSchema/${encodeURIComponent(contactType)}`;
    let payload = { screen: screen, email: email };
    if (token) {
      payload = { ...payload, ...{ token } };
    }
    return this.http.post<T>(url, payload, httpOptions);
  }

  verifyEmail(
    email: string,
    contactType: string,
    language: string,
    recaptChaV3Token: string = ''
  ): Observable<any> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'en-namespace': environment.namespace,
      browserLanguage: language,
    });

    if (recaptChaV3Token?.length > 0) {
      headers = headers.set('recaptcha-v3-token', recaptChaV3Token);
    }

    const httpOptions = {
      headers: headers,
    };
    const url =
      this.baseUrl + `contact/verifyEmail/${encodeURIComponent(contactType)}`;
    return this.http.post(url, { email: email }, httpOptions);
  }

  getDomainAndSiteKey(): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'en-namespace': environment.namespace,
      }),
    };
    const url = this.baseUrl + `contact/recaptchaDetails`;
    return this.http.get<any>(url, httpOptions).pipe(
      map((result) => {
        this.isDomainNotAupr = get(result, 'recaptchaSupported', false);
        if (this.isDomainNotAupr) {
          // only if domain is not aupr, fetch site key
          this.siteKey = get(result, 'siteKey', null);
        }
        return result;
      }),
      catchError((err) => {
        // this will occur only for non-aupr regions if external service failed to fetch site key
        this.siteKey = null;
        this.isDomainNotAupr = false;
        this.recaptchaKeysFailure = true;
        return of();
      })
    );
  }

  getReCaptchaV3Token$(
    recaptchaV3Service: ReCaptchaV3Service,
    action: string = 'submit'
  ): Observable<string> {
    return recaptchaV3Service ? recaptchaV3Service.execute(action) : of('');
  }

  submitForm$(
    formTip: string,
    unfilteredFormData: any,
    typeFields: IField[],
    recaptChaV3Token: string = '',
    token = ''
  ): Observable<any> {
    // Incredibly hacky... *sigh*
    const propertiesToOmit = [];
    Object.keys(unfilteredFormData).forEach((element) => {
      // if property is uuid || if value equals null or undefined
      if (
        /^[0-9a-f]{8}-([0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(element) ||
        isNil(unfilteredFormData[element])
      ) {
        propertiesToOmit.push(element); // we will remove null fields from the dto
      } else {
        // if non-null .. do the extra handling for date & date-time fields
        const fieldInfo = typeFields.find((field) => element === field.$tip);
        if (
          fieldInfo &&
          fieldInfo.format === 'date-time' &&
          moment(unfilteredFormData[element]).isValid()
        ) {
          unfilteredFormData[element] =
            fieldInfo.isDateOnly === true
              ? moment(unfilteredFormData[element]).startOf('day').format() // for date .. set hh:mm to 00:00
              : moment(unfilteredFormData[element]).format();
        }
      }
    });

    const formData = omit(cloneDeep(unfilteredFormData), propertiesToOmit);
    formData['token'] = token;
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'en-namespace': environment.namespace,
    });

    if (recaptChaV3Token?.length > 0) {
      headers = headers.set('recaptcha-v3-token', recaptChaV3Token);
    }
    const httpOptions = {
      headers: headers,
    };
    const url =
      environment.externalServiceUrl +
      'contact/submitForm/' +
      encodeURIComponent(formTip);
    return this.http.post<any>(url, formData, httpOptions);
  }

  tokenCheck(token): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'en-namespace': environment.namespace,
      }),
    };
    const url = this.baseUrl + `contact/token-check`;
    return this.http.post<any>(url, token, httpOptions);
  }

  updateCurrentsharedFormSettings(data: any) {
    this.sharedFormSettings.next(data);
  }

  updateCurrentContactTypeData(data: any) {
    this.contactTypeData.next(data);
  }

  /**
   *
   * @param fileTip - tip of the file whose information needs to be fetched
   * @returns
   */
  getFileData(fileTip: string): Observable<IFileData> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'en-namespace': environment.namespace,
      }),
    };

    const url = this.baseUrl + `contact/fileData`;
    return this.http.post<any>(url, { fileTip }, httpOptions);
  }

  downloadFile(
    fileTip: string,
    fieldTip: string,
    token: string = ''
  ): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'en-namespace': environment.namespace,
      }),
    };

    const url = this.baseUrl + `contact/downloadFile/${fileTip}`;
    return this.http.post<any>(url, { token, fieldTip }, httpOptions);
  }

  fetchObjectChooserValuesViaTips(
    tips: string | string[],
    token: string = null
  ): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'en-namespace': environment.namespace,
      }),
    };

    const url = this.baseUrl + `contact/objectChooser/value`;
    let payload = { tips };
    if (token) {
      payload = { ...payload, ...{ token } };
    }
    return this.http.post<any>(url, payload, httpOptions);
  }

  getBrowserLang(): string {
    language = this.translate.getBrowserCultureLang();
    if (language === 'zh-TW') {
      language = 'zh-Hant';
    }
    if (language === 'zh-CN') {
      language = 'zh';
    }
    return language;
  }
}
